import React, { useState, useEffect, useRef, useCallback } from 'react';

const SearchFilters = ({ onSearch, searchFilters, isFiltering }) => {
    const [localFilters, setLocalFilters] = useState(searchFilters);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleFilterChange = (key, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSearch = () => {
        onSearch(localFilters);
    };

    const handleClear = () => {
        const emptyFilters = {
            zipCode: '',
            city: '',
            minLandValue: '',
            maxLandValue: '',
            minArea: '',
            maxArea: ''
        };
        setLocalFilters(emptyFilters);
        onSearch(emptyFilters);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Search for a Land Parcel</h3>
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    {isExpanded ? 'Less Filters' : 'More Filters'}
                </button>
            </div>
            
            <div className="space-y-3">
                {/* Always visible filters */}
                <div className="grid grid-cols-2 gap-3">
                    <input
                        type="text"
                        placeholder="ZIP Code"
                        value={localFilters.zipCode}
                        onChange={(e) => handleFilterChange('zipCode', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                        type="text"
                        placeholder="City"
                        value={localFilters.city}
                        onChange={(e) => handleFilterChange('city', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Expandable filters */}
                {isExpanded && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="number"
                                placeholder="Min Land Value"
                                value={localFilters.minLandValue}
                                onChange={(e) => handleFilterChange('minLandValue', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                                type="number"
                                placeholder="Max Land Value"
                                value={localFilters.maxLandValue}
                                onChange={(e) => handleFilterChange('maxLandValue', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="number"
                                placeholder="Min Area (sq ft)"
                                value={localFilters.minArea}
                                onChange={(e) => handleFilterChange('minArea', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                                type="number"
                                placeholder="Max Area (sq ft)"
                                value={localFilters.maxArea}
                                onChange={(e) => handleFilterChange('maxArea', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 pt-2">
                    <button
                        onClick={handleSearch}
                        disabled={isFiltering}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isFiltering ? 'Searching...' : 'Search'}
                    </button>
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                    >
                        Clear
                    </button>
                </div>
            </div>
        </div>
    );
};

const LandParcelCard = ({ parcel }) => {
    return (
        <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200 border border-gray-200">
            <div className="space-y-2">
                <div className="flex justify-between items-start">
                    <h4 className="font-semibold text-gray-800 text-sm truncate flex-1">
                        {parcel.siteaddres || 'No address available'}
                    </h4>
                    <span className="text-xs text-gray-500 ml-2">ID: {parcel.id}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                        <span className="font-medium">City:</span> {parcel.sitecityst || 'N/A'}
                    </div>
                    <div>
                        <span className="font-medium">ZIP:</span> {parcel.sitezip5 || 'N/A'}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                        <span className="font-medium">Land Value:</span>
                        <br />
                        <span className="text-green-600 font-semibold">
                            ${parseInt(parcel.landval || 0).toLocaleString()}
                        </span>
                    </div>
                    <div>
                        <span className="font-medium">Area:</span>
                        <br />
                        <span className="text-blue-600 font-semibold">
                            {Math.round(parcel.shape_area || 0).toLocaleString()} sq ft
                        </span>
                    </div>
                </div>

                {parcel.legaldescr && (
                    <div className="text-xs text-gray-500 border-t pt-2">
                        <span className="font-medium">Legal:</span> {parcel.legaldescr}
                    </div>
                )}
            </div>
        </div>
    );
};

const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
    </div>
);

export const LandParcelSuggestionPanel = ({
    parcels = [],
    loading = false,
    hasMoreData = false,
    totalCount = 0,
    onLoadMore,
    onSearch,
    searchFilters = {},
    isFiltering = false
}) => {
    const scrollContainerRef = useRef(null);
    const [isNearBottom, setIsNearBottom] = useState(false);

    // Infinite scroll detection
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
        
        setIsNearBottom(isNearBottom);

        if (isNearBottom && hasMoreData && !loading && onLoadMore) {
            onLoadMore();
        }
    }, [hasMoreData, loading, onLoadMore]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    return (
        <div className="flex flex-col h-full">
            {/* Search Filters */}
            <SearchFilters 
                onSearch={onSearch}
                searchFilters={searchFilters}
                isFiltering={isFiltering}
            />

            {/* Scrollable Parcel List */}
            <div 
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto space-y-3 pr-2"
                style={{ scrollBehavior: 'smooth' }}
            >
                {isFiltering && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Searching...</span>
                    </div>
                )}

                {!isFiltering && parcels.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-500">
                        <p>No land parcels found.</p>
                        <p className="text-sm mt-1">Try adjusting your search filters.</p>
                    </div>
                )}

                {!isFiltering && parcels.map((parcel) => (
                    <LandParcelCard key={parcel.id} parcel={parcel} />
                ))}

                {/* Loading indicator for infinite scroll */}
                {loading && !isFiltering && <LoadingSpinner />}

                {/* End of results indicator */}
                {!hasMoreData && parcels.length > 0 && !loading && (
                    <div className="text-center py-4 text-gray-500 text-sm border-t">
                        End of results • {totalCount.toLocaleString()} total parcels
                    </div>
                )}
            </div>
        </div>
    );
};